"use client";

import { useCallback } from "react";
import { useMotionValue } from "motion/react";

import { captureEvent } from "@/app/_analytics/analytics";
import { useNativeAudio } from "@/app/_providers/native-audio/NativeAudioProvider";
import { EventName } from "@mw/shared/analyticsEventNames";

interface UseSTTHeroAudioControlsOptions {
  audioId: string;
  audioUrl: string | undefined;
  sampleName: string | undefined;
  contentAnalyticsProps: { content_type: "user_upload" } | { content_type: "example"; example_index: number };
}

export function useSTTHeroAudioControls({
  audioId,
  audioUrl,
  sampleName,
  contentAnalyticsProps,
}: UseSTTHeroAudioControlsOptions) {
  const currentTime = useMotionValue(0);
  const { togglePlay: audioTogglePlay, playingId, seek, stop: audioStop } = useNativeAudio();
  const isPlaying = playingId === audioId;

  const resetPlayback = useCallback(() => {
    audioStop();
    currentTime.set(0);
  }, [audioStop, currentTime]);

  const reportPlaybackStarted = useCallback(() => {
    captureEvent(EventName.mw_stt_example_played, contentAnalyticsProps);
  }, [contentAnalyticsProps]);

  const handleTimeUpdate = useCallback(
    (time: number) => {
      currentTime.set(time);
    },
    [currentTime],
  );

  const handleEnded = useCallback(() => {
    currentTime.set(0);
  }, [currentTime]);

  const toggleCurrentAudio = useCallback(
    (startTime?: number, shouldReportPlayback = false) => {
      if (!audioUrl) return;

      if (shouldReportPlayback) {
        reportPlaybackStarted();
      }

      audioTogglePlay({
        id: audioId,
        url: audioUrl,
        ...(startTime === undefined ? {} : { startTime }),
        onTimeUpdate: handleTimeUpdate,
        onEnded: handleEnded,
        trackingProps: { source: "stt_hero_demo", sample_name: sampleName },
      });
    },
    [audioId, audioTogglePlay, audioUrl, handleEnded, handleTimeUpdate, reportPlaybackStarted, sampleName],
  );

  const togglePlay = useCallback(() => {
    if (!audioUrl) return;
    toggleCurrentAudio(undefined, !isPlaying);
  }, [audioUrl, isPlaying, toggleCurrentAudio]);

  const stop = useCallback(() => {
    resetPlayback();
  }, [resetPlayback]);

  const seekToTime = useCallback(
    (time: number) => {
      seek(time);
      if (!isPlaying && audioUrl) {
        toggleCurrentAudio(time, true);
      }
    },
    [seek, isPlaying, audioUrl, toggleCurrentAudio],
  );

  return {
    currentTime,
    isPlaying,
    togglePlay,
    stop,
    seekToTime,
    resetPlayback,
  };
}
